import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, NEVER, Observable, firstValueFrom, from } from 'rxjs';
import { catchError, map, mergeMap, take, tap } from 'rxjs/operators';
import { ServerEventsProviderService } from '@anecdotes/server-events';
import { LoggerService } from '@anecdotes/utils';
import { ServerEventType, ThreadModel, ThreadStateEnum } from '../../../models';
import { OperationTrackerService } from '../../../services';
import { ThreadApiService } from '../../../services/api';
import { ThreadSelectors, selectThreadEntities } from '../../../store/selectors';
import { ThreadAction } from '../../actions';
import { CommentingState } from '../../state';

@Injectable()
export class ThreadEffects {
  constructor(
    private actions: Actions,
    private threadApiService: ThreadApiService,
    private operationTrackerService: OperationTrackerService,
    private store: Store<CommentingState>,
    private serverEventsProviderService: ServerEventsProviderService,
    private logger: LoggerService,
  ) {}

  loadAllThreads$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(ThreadAction.LoadAll),
        mergeMap(() => this.threadApiService.getAllThreads()),
        tap((response) => this.store.dispatch(ThreadAction.AllLoaded({ threads: response }))),
      ),
    { dispatch: false },
  );

  createThread$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(ThreadAction.Create),
        mergeMap((action) =>
          this.operationTrackerService
            .passThroughOperationTracking(
              action.operationId,
              this.threadApiService.createThread(action.thread).pipe(
                tap((createdThread) =>
                  this.store.dispatch(
                    ThreadAction.Created({
                      thread: createdThread,
                    }),
                  ),
                ),
              ),
            )
            .pipe(
              catchError(() => {
                this.store.dispatch(ThreadAction.CreateFailed({ thread: action.thread }));
                return NEVER;
              }),
            ),
        ),
      ),
    { dispatch: false },
  );

  updateThreadBody$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(ThreadAction.UpdateBody),
        mergeMap((action) =>
          this.createUpdateStream(action.operationId, action.threadId, (oldThread) => ({
            ...oldThread,
            body: action.newBody,
            mentionedEmails: action.mentionedEmails,
            extraParams: action.extraParams,
          })),
        ),
      ),
    { dispatch: false },
  );

  deleteSingleThread$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(ThreadAction.DeleteSingle),
        mergeMap((action) => {
          return this.store.select(selectThreadEntities).pipe(
            take(1),
            map((threadEntities) => ({
              action,
              deletedThread: threadEntities[action.threadId],
            })),
            tap((action) => this.store.dispatch(ThreadAction.Deleted({ deletedThreads: [action.deletedThread] }))),
            mergeMap((aggregated) =>
              this.operationTrackerService
                .passThroughOperationTracking(
                  aggregated.action.operationId,
                  this.threadApiService.deleteThread(aggregated.action.threadId),
                )
                .pipe(
                  catchError(() => {
                    this.store.dispatch(
                      ThreadAction.DeleteFailed({
                        deletedThreads: [aggregated.deletedThread],
                      }),
                    );
                    return EMPTY;
                  }),
                ),
            ),
          );
        }),
      ),
    { dispatch: false },
  );

  bulkDeleteThreads$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(ThreadAction.DeleteBatch),
        mergeMap((action) => {
          return this.store.select(selectThreadEntities).pipe(
            take(1),
            map((threadEntities) => ({
              operationId: action.operationId,
              threadIdsToDelete: action.threadIdsToDelete,
              deletedThreads: action.threadIdsToDelete.map((id) => threadEntities[id]),
            })),
            tap((aggregated) =>
              this.store.dispatch(ThreadAction.Deleted({ deletedThreads: aggregated.deletedThreads })),
            ),
            mergeMap((aggregated) =>
              this.operationTrackerService
                .passThroughOperationTracking(
                  aggregated.operationId,
                  this.threadApiService.bulkDelete(aggregated.threadIdsToDelete),
                )
                .pipe(
                  catchError(() => {
                    this.store.dispatch(
                      ThreadAction.DeleteFailed({
                        deletedThreads: aggregated.deletedThreads,
                      }),
                    );
                    return NEVER;
                  }),
                ),
            ),
          );
        }),
      ),
    { dispatch: false },
  );

  activateThread$ = createEffect(
    () =>
      this.actions.pipe(ofType(ThreadAction.ActivateSingle)).pipe(
        mergeMap((action) =>
          this.createUpdateStream(action.operationId, action.threadId, (oldThread) => ({
            ...oldThread,
            state: ThreadStateEnum.Active,
          })),
        ),
      ),
    { dispatch: false },
  );

  resolveThread$ = createEffect(
    () =>
      this.actions.pipe(ofType(ThreadAction.ResolveSingle)).pipe(
        mergeMap((action) =>
          this.createUpdateStream(action.operationId, action.threadId, (oldThread) => ({
            ...oldThread,
            state: ThreadStateEnum.Resolved,
          })),
        ),
      ),
    { dispatch: false },
  );

  bulkResolveThreads$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(ThreadAction.ResolveBatch),
        mergeMap((action) => {
          return this.store.select(selectThreadEntities).pipe(
            take(1),
            map((threadEntities) => ({
              operationId: action.operationId,
              threadIdsToResolve: action.threadIdsToResolve,
              threadsBeforeUpdate: action.threadIdsToResolve.map((id) => threadEntities[id]),
            })),
            tap((aggregated) =>
              this.store.dispatch(
                ThreadAction.Updated({
                  updatedThreads: aggregated.threadsBeforeUpdate.map((thread) => ({
                    ...thread,
                    state: ThreadStateEnum.Resolved,
                  })),
                }),
              ),
            ),
            mergeMap((aggregated) =>
              this.operationTrackerService
                .passThroughOperationTracking(
                  aggregated.operationId,
                  this.threadApiService.bulkResolve(aggregated.threadIdsToResolve),
                )
                .pipe(
                  catchError(() => {
                    this.store.dispatch(
                      ThreadAction.UpdateFailed({
                        threadsBeforeUpdate: aggregated.threadsBeforeUpdate,
                      }),
                    );
                    return NEVER;
                  }),
                ),
            ),
          );
        }),
      ),
    { dispatch: false },
  );

  bulkActivateThreads$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(ThreadAction.ActivateBatch),
        mergeMap((action) => {
          return this.store.select(selectThreadEntities).pipe(
            take(1),
            map((threadEntities) => ({
              operationId: action.operationId,
              threadIdsToActivate: action.threadIdsToActivate,
              threadsBeforeUpdate: action.threadIdsToActivate.map((id) => threadEntities[id]),
            })),
            tap((aggregated) =>
              this.store.dispatch(
                ThreadAction.Updated({
                  updatedThreads: aggregated.threadsBeforeUpdate.map((thread) => ({
                    ...thread,
                    state: ThreadStateEnum.Active,
                  })),
                }),
              ),
            ),
            mergeMap((aggregated) =>
              this.operationTrackerService
                .passThroughOperationTracking(
                  aggregated.operationId,
                  this.threadApiService.bulkActivate(aggregated.threadIdsToActivate),
                )
                .pipe(
                  catchError(() => {
                    this.store.dispatch(
                      ThreadAction.UpdateFailed({
                        threadsBeforeUpdate: aggregated.threadsBeforeUpdate,
                      }),
                    );
                    return NEVER;
                  }),
                ),
            ),
          );
        }),
      ),
    { dispatch: false },
  );

  threadCreatedEvent$ = createEffect(
    () =>
      this.serverEventsProviderService.getServerEvent(ServerEventType.ThreadCreated).pipe(
        tap((serverEvent) => {
          this.logger.log({
            ServerEventMessage: serverEvent,
          });
        }),
        tap((createEvent) => this.store.dispatch(ThreadAction.Created({ thread: createEvent.message_object.notification.body }))),
      ),
    { dispatch: false },
  );

  threadUpdatedEvent$ = createEffect(
    () =>
      this.serverEventsProviderService.getServerEvent(ServerEventType.ThreadUpdated).pipe(
        tap((serverEvent) => {
          this.logger.log({
            ServerEventMessage: serverEvent,
          });
        }),
        tap((updateEvent) => this.store.dispatch(ThreadAction.Updated({ updatedThreads: [updateEvent.message_object.notification.body] }))),
      ),
    { dispatch: false },
  );

  threadDeletedEvent$ = createEffect(
    () =>
      this.serverEventsProviderService.getServerEvent(ServerEventType.ThreadDeleted).pipe(
        tap((serverEvent) => {
          this.logger.log({
            ServerEventMessage: serverEvent,
          });
        }),
        tap((deleteEvent) => this.store.dispatch(ThreadAction.Deleted({ deletedThreads: [deleteEvent.message_object.notification.body] }))),
      ),
    { dispatch: false },
  );

  private createUpdateStream(
    operationId: string,
    threadIdToUpdate: string,
    constructNewThreadCallback: (oldThread: ThreadModel) => ThreadModel,
  ): Observable<void> {
    return this.operationTrackerService
      .passThroughOperationTracking(
        operationId,
        from(
          (async () => {
            const threadBeforeUpdate = await firstValueFrom(
              this.store.select(ThreadSelectors.createSelectorById(threadIdToUpdate)),
            );

            const newThread: ThreadModel = constructNewThreadCallback(threadBeforeUpdate);
            this.store.dispatch(ThreadAction.Updated({ updatedThreads: [newThread] }));

            try {
              await firstValueFrom(this.threadApiService.updateThread(newThread));
            } catch (err) {
              this.store.dispatch(
                ThreadAction.UpdateFailed({
                  threadsBeforeUpdate: [threadBeforeUpdate],
                }),
              );
              throw err;
            }
          })(),
        ),
      )
      .pipe(catchError(() => NEVER));
  }
}
